import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@totalsoft/rocket-ui';
import Income from '@mui/icons-material/PlaylistAddCheck';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import GenericEntityFlowEditTransitionsListComponent from './GenericEntityFlowEditTransitionsListComponent';
import { UPDATE_GENERIC_ENTITY_FLOW_TRANSITION } from '../mutations/UpdateGenericEntityFlowTransition';
import { DELETE_GENERIC_ENTITY_FLOW_TRANSITION } from '../mutations/DeleteGenericEntityFlowTransition';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { isDirty } from '@totalsoft/change-tracking';
import { get, over } from '@totalsoft/change-tracking-react';
import { GENERIC_ENTITY_FLOW_TRANSITION_CHANGED, GENERIC_ENTITY_FLOW_TRANSITION_DELETED } from '../subscriptions/transitionSubscriptions';
import { GENERIC_ENTITY_FLOW_TRANSITIONS_QUERY, GENERIC_ENTITY_FLOW_QUERY, GENERIC_ENTITY_BUSINESS_STATES_QUERY } from '../../list/queries';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { useToast } from 'hooks/toasts/useToast';
import { append, remove } from 'ramda';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { required, shape, concatFailure, items, fromModel } from '@totalsoft/pure-validations';
import { Validator, Success, Failure, ValidationError } from '@totalsoft/pure-validations';
import { isPropertyDirty } from '@totalsoft/change-tracking';
import { emptyArray } from 'utils/constants';
import IconButton from 'components/common/buttons/IconButton';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { FlowPermissionType, useFlowPermissions } from '../hooks';
import { usePermissions } from 'providers/RightsHooks';

export const uniqueTransition = list =>
  Validator(function uniqueTransition() {
    if (!list || list?.length === 0) return Success;

    const item = list.find(x => !x.transitionId);
    if (!item) return Success;

    const duplicated = list.find(x => x.initialStateId === item.initialStateId && x.finalStateId === item.finalStateId && x.transitionId);
    if (duplicated) return Failure(ValidationError(i18next.t('GenericEntityFlow.GenericEntityFlowTransitionUnique')));
    else return Success;
  });

export const validatorTransitions = fromModel(parent =>
  concatFailure(
    items(
      fromModel(model =>
        shape({
          initialStateId: concatFailure(
            required,
            Validator(x =>
              !x || x !== model.finalStateId
                ? Success
                : Failure(ValidationError(i18next.t('GenericEntityFlow.GenericEntityFlowTransitionInitialAndFinalStateUnique')))
            )
          ),
          finalStateId: concatFailure(
            required,
            Validator(x =>
              !x || x !== model.initialStateId
                ? Success
                : Failure(ValidationError(i18next.t('GenericEntityFlow.GenericEntityFlowTransitionInitialAndFinalStateUnique')))
            )
          )
        })
      )
    ),
    uniqueTransition(parent)
  )
);

const GenericEntityFlowEditTransitionsContainer = ({ flowId, transitionsLens, transitionsDirtyInfo, resetTransitions }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [transitionsValidation, validateTransitions] = useDirtyFieldValidation(validatorTransitions);
  const [saving, setSaving] = useState(false);

  const authorizationCode = useFlowPermissions(FlowPermissionType.WRITE);
  const canWrite = usePermissions(authorizationCode);

  //#region QUERIES
  const { data: genericEntityFlowData } = useQuery(GENERIC_ENTITY_FLOW_QUERY, {
    variables: { flowId: flowId },
    skip: !flowId,
    fetchPolicy: 'cache-only'
  });

  const { data: genericEntityBusinessStatesData } = useQuery(GENERIC_ENTITY_BUSINESS_STATES_QUERY);

  const { loading } = useQuery(GENERIC_ENTITY_FLOW_TRANSITIONS_QUERY, {
    variables: { flowId: flowId },
    skip: !flowId,
    onCompleted: data => {
      resetTransitions({
        genericEntityFlowTransitions: [...data.genericEntityFlowTransitions]
      });
    }
  });

  const { data: userGroupsData } = useQuery(USER_GROUPS_QUERY, {
    variables: { flowId: flowId },
    skip: !flowId
  });
  //#endregion QUERIES

  const filteredFlowStates = useMemo(() => {
    const businessStates = genericEntityBusinessStatesData?.genericEntityBusinessStates ?? emptyArray;
    const flowStates = genericEntityFlowData?.genericEntityFlow?.genericEntityFlowStates ?? emptyArray;
    return businessStates.filter(({ stateId }) => flowStates.some(x => x.stateId === stateId));
  }, [genericEntityBusinessStatesData?.genericEntityBusinessStates, genericEntityFlowData?.genericEntityFlow?.genericEntityFlowStates]);

  //#region Auxiliary functions
  const deleteLocalTransition = useCallback(
    index => {
      let otherAreDirty = false;
      (transitionsLens.genericEntityFlowTransitions |> get).forEach((_, idx) => {
        if (idx !== index && !otherAreDirty) {
          const propertyPath = 'genericEntityFlowTransitions[' + idx.toString() + ']';
          otherAreDirty = isPropertyDirty(propertyPath, transitionsDirtyInfo);
        }
      });

      if (otherAreDirty) over(transitionsLens.genericEntityFlowTransitions, remove(index, 1));
      else {
        (transitionsLens.genericEntityFlowTransitions |> get).splice(index, 1);
        resetTransitions({
          genericEntityFlowTransitions: [...(transitionsLens.genericEntityFlowTransitions |> get)]
        });
      }
      addToast(t('GenericEntityFlow.TransitionDeleted'), 'success');
    },
    [transitionsLens.genericEntityFlowTransitions, transitionsDirtyInfo, resetTransitions, addToast, t]
  );
  //#endregion Auxiliary functions

  //#region MUTATIONS
  const [updateGenericEntityFlowTransition] = useMutation(UPDATE_GENERIC_ENTITY_FLOW_TRANSITION, {
    onError: () => {
      setSaving(false);
    }
  });
  //#endregion MUTATIONS

  //#region SUBSCRIPTIONS
  useSubscription(GENERIC_ENTITY_FLOW_TRANSITION_CHANGED, {
    onError: () => {
      setSaving(false);
    },
    onData: ({ data: { data }, client }) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: `genericEntityFlowTransitions({"flowId":${flowId}})` });
      client.cache.gc();
      addToast(t('GenericEntityFlow.TransitionSaved'), 'success');
      setSaving(false);
      resetTransitions({
        genericEntityFlowTransitions: [...data.genericEntityFlowTransitionChanged]
      });
    }
  });

  useSubscription(GENERIC_ENTITY_FLOW_TRANSITION_DELETED, {
    onData: ({
      data: {
        data: {
          genericEntityFlowTransitionDeleted: { transitionId }
        }
      },
      client
    }) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityFlowTransitions' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'computeFinancialProductNextStates' });
      client.cache.gc();
      addToast(t('GenericEntityFlow.TransitionDeleted'), 'success');
      resetTransitions(prevState => ({
        ...prevState,
        genericEntityFlowTransitions: prevState.genericEntityFlowTransitions.filter(x => x.transitionId !== transitionId)
      }));
    }
  });
  //#endregion SUBSCRIPTIONS

  const handleSaveTransition = useCallback(
    transitionLens => {
      setSaving(true);
      const transition = transitionLens |> get;
      const { ...input } = transition;

      if (!validateTransitions(transition.genericEntityFlowTransitions)) {
        setSaving(false);
        return;
      }
      updateGenericEntityFlowTransition({ variables: { input } });
    },
    [updateGenericEntityFlowTransition, validateTransitions]
  );

  useEffect(() => {
    validateTransitions(transitionsLens.genericEntityFlowTransitions |> get, transitionsDirtyInfo);
  }, [validateTransitions, transitionsLens, transitionsDirtyInfo]);

  const [deleteGenericEntityFlowTransition] = useMutation(DELETE_GENERIC_ENTITY_FLOW_TRANSITION);

  const handleAddTransition = useCallback(() => {
    over(
      transitionsLens.genericEntityFlowTransitions,
      append({
        transitionId: undefined,
        siteId: 0,
        flowId: flowId,
        allowedForAllUserGroups: true,
        fourEyesPrinciple: false,
        requestBusinessMomentOnChange: false,
        requestReasonOnChange: false,
        saveHistory: false
      })
    );
  }, [transitionsLens.genericEntityFlowTransitions, flowId]);

  const handleDeleteTransition = useCallback(
    index => {
      if (window.confirm(t('GenericEntityFlow.DeleteTransitionQuestion'))) {
        const transition = (transitionsLens.genericEntityFlowTransitions |> get)[index];
        if (transition.transitionId) {
          const input = { transitionId: transition.transitionId };
          deleteGenericEntityFlowTransition({ variables: { input } });
        } else {
          deleteLocalTransition(index);
        }
      }
    },
    [transitionsLens.genericEntityFlowTransitions, deleteGenericEntityFlowTransition, deleteLocalTransition, t]
  );

  return (
    <Card
      icon={Income}
      iconColor='primary'
      title={t('GenericEntityFlow.Transitions')}
      actions={<IconButton type='add' onClick={handleAddTransition} disabled={!canWrite || isDirty(transitionsDirtyInfo)} />}
    >
      {(!flowId || loading) && <WrappedFakeText />}
      {flowId && !loading && (
        <GenericEntityFlowEditTransitionsListComponent
          flowId={flowId}
          transitionsLens={transitionsLens}
          transitionsDirtyInfo={transitionsDirtyInfo}
          businessStates={filteredFlowStates}
          onSaveTransition={handleSaveTransition}
          userGroups={userGroupsData?.userGroupsConfig || emptyArray}
          handleDeleteTransition={handleDeleteTransition}
          transitionsValidation={transitionsValidation}
          saving={saving}
        />
      )}
    </Card>
  );
};

GenericEntityFlowEditTransitionsContainer.propTypes = {
  flowId: PropTypes.number,
  transitionsLens: PropTypes.object,
  transitionsDirtyInfo: PropTypes.object,
  resetTransitions: PropTypes.func
};

export default GenericEntityFlowEditTransitionsContainer;
